import type { FC, PropsWithChildren } from 'react';
import React from 'react';

import { AppLayoutBreakpoint, useContainerWidth } from '../../../../util/responsiveness';
import Flex from '../../../Flex';
import {
  useAppLayoutContext,
  useAppLayoutGutterSize,
  useAppLayoutWidth,
  useContentContainerContext,
  useContentMaxWidth,
  useIsContentSidebarCurrentlyHiddenByNarrowWidth,
} from '../../hooks';

import { useClassName } from './MainAreaRowContent.styles';
import type { MainAreaRowContentProps } from './MainAreaRowContent.types';
import SidebarPanelButtonStickyColumn from './SidebarPanelButtonStickyColumn';
import { shouldShowSubheaderAtWidth } from './SubHeaderRow';

const MainAreaRowContent: FC<PropsWithChildren<MainAreaRowContentProps>> = ({ hasHeroBackground, children }) => {
  const gutterSize = useAppLayoutGutterSize();
  const contentMaxWidth = useContentMaxWidth();
  const { shouldFillAvailableSpace } = useContentContainerContext();
  const appLayoutContext = useAppLayoutContext();
  const height = shouldFillAvailableSpace ? '100%' : undefined;
  const maxWidth = shouldFillAvailableSpace ? '100%' : contentMaxWidth;
  const appLayoutWidth = useAppLayoutWidth();
  const isSidebarSuppressed = useIsContentSidebarCurrentlyHiddenByNarrowWidth();
  const shouldSidebarPanelButtonBeDisplayedAtBreakpoint = !shouldShowSubheaderAtWidth(
    appLayoutWidth,
    appLayoutContext?.isLeftNavCollapsible ?? false
  );
  const sidebarPanelButton =
    !appLayoutContext?.pinnedCollapsibleRightRail &&
    isSidebarSuppressed &&
    shouldSidebarPanelButtonBeDisplayedAtBreakpoint ? (
      <SidebarPanelButtonStickyColumn />
    ) : null;

  const containerWidth = useContainerWidth();
  const paddingTop =
    shouldFillAvailableSpace ||
    appLayoutContext?.removeContentPadding ||
    window.innerWidth <= AppLayoutBreakpoint.Width568
      ? 0
      : gutterSize;

  const className = useClassName({
    hasHeroBackground,
    containerWidth,
  });

  return (
    <Flex
      justifyContent="center"
      grow={1}
      paddingTop={paddingTop}
      className={className}
      height={height}
      minHeight="100%"
    >
      <Flex direction="column" rowGap={gutterSize} grow={1} maxWidth={maxWidth}>
        {children}
      </Flex>
      {sidebarPanelButton}
    </Flex>
  );
};

export default MainAreaRowContent;
